import { ChangeEvent, FC, ReactElement, ReactNode, useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  Divider,
  IconButton,
  Input,
  InputBaseProps,
  Select,
  SelectChangeEvent,
  Stack,
  Theme,
  Typography,
} from '@mui/material';
import { SxProps } from '@mui/system';
import { Add as AddIcon, Cancel as CancelIcon, Check as CheckIcon } from '@mui/icons-material';

import dayjs, { Dayjs } from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';

import type { InfoFieldChangeHandler } from './types';

type EditProps = {
  withEditMode: boolean;
  isEditMode: boolean;
};

type ComponentType =
  | 'input'
  | 'select'
  | 'multipleSelect'
  | 'date'
  | 'textarea'
  | 'chip'
  | 'checkbox';

type InputProps = {
  name: string;
  inputType?: InputBaseProps['type'];
  value: string | string[] | number | boolean | null;
  onChange: InfoFieldChangeHandler;
  componentType?: ComponentType;
  selectOptions?: ReactElement | JSX.Element[];
  mitTextAreaRows?: number;
  disable?: boolean;
};

type InfoFieldProps = {
  name: string;
  value: string | string[] | number | boolean | JSX.Element | null | undefined;
  formattedValue?: string | JSX.Element;
  component?: ReactNode;
  hint?: ReactNode;
  editProps?: EditProps;
  inputProps?: InputProps;
  withoutDivider?: boolean;
  breakSpaces?: boolean;
  withEmptyValue?: boolean;
  nameSx?: SxProps<Theme>;
  isLoading?: boolean;
  maxWidthValue?: string;
  placeholder?: string;
  hidden?: boolean;
};

export const InfoField: FC<InfoFieldProps> = (props) => {
  const {
    name,
    value,
    formattedValue,
    component,
    hint,
    editProps,
    inputProps,
    breakSpaces,
    withoutDivider,
    withEmptyValue,
    nameSx,
    isLoading,
    maxWidthValue,
    placeholder,
    hidden,
  } = props;

  // variables
  const componentType = inputProps?.componentType ?? 'input';
  const isChip = componentType === 'chip';

  // chip logic
  const [addChipInputShown, setAddChipInputShown] = useState(false);
  const [newChipValue, setNewChipValue] = useState('');
  const [chipArray, setChipArray] = useState<string[]>([]);

  useEffect(() => {
    if (!isChip || !Array.isArray(value)) return;
    setChipArray(value);
  }, [editProps?.isEditMode]);

  useEffect(() => {
    if (!editProps) return;
    if (editProps.isEditMode) return;

    if (!editProps.isEditMode) setChipArray([]);
  }, [editProps?.isEditMode]);

  const showAddChipInput = () => {
    setAddChipInputShown(true);
  };
  const hideAddChipInput = () => {
    setAddChipInputShown(false);
    setNewChipValue('');
  };

  const saveNewChipValue = () => {
    if (!isChip || !Array.isArray(value)) return;

    const newValue = [...chipArray, newChipValue];

    setChipArray(newValue);
    inputProps?.onChange(newValue, true, inputProps?.name);
    hideAddChipInput();
  };

  // handlers
  const onChangeHandler = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string | string[]>
  ) => {
    inputProps?.onChange && inputProps.onChange(e);
  };

  const onChangeHandlerForDate = (
    newValue: Dayjs | null,
    isDate: boolean,
    dateFieldName: string | undefined
  ) => {
    inputProps?.onChange && inputProps.onChange(newValue, isDate, dateFieldName);
  };

  // elements
  const getChipElements = (withDelete?: boolean) => {
    if (!isChip || !Array.isArray(value)) return;

    const onDelete = (item: string) => {
      const newValue = chipArray.filter((it) => it !== item);
      setChipArray(newValue);
      inputProps?.onChange(newValue, true, inputProps?.name);
    };

    const array = editProps?.isEditMode ? chipArray : value;

    return array.map((it, index) => {
      return <Chip key={index} label={it} onDelete={withDelete ? () => onDelete(it) : undefined} />;
    });
  };

  const editComponentMap: Record<ComponentType, ReturnType<FC>> = {
    input: (
      <Input
        name={inputProps?.name}
        type={inputProps?.inputType}
        value={inputProps?.value}
        onChange={(e) => onChangeHandler(e)}
        sx={{ maxWidth: '298px' }}
        fullWidth
        placeholder={placeholder}
      />
    ),
    checkbox: (
      <Checkbox
        name={inputProps?.name}
        checked={!!inputProps?.value}
        onChange={(e) => onChangeHandler(e)}
        sx={{ maxWidth: '298px' }}
        disabled={inputProps?.disable}
      />
    ),
    select: (
      <Select
        name={inputProps?.name}
        value={inputProps?.value as string}
        onChange={(e) => onChangeHandler(e)}
        variant="standard"
        sx={{ maxWidth: '298px' }}
        fullWidth
        // placeholder={placeholder}
      >
        {inputProps?.selectOptions}
      </Select>
    ),
    multipleSelect: (
      <Select
        multiple
        name={inputProps?.name}
        value={inputProps?.value as string[]}
        onChange={(e) => onChangeHandler(e)}
        variant="standard"
        sx={{ maxWidth: '298px' }}
        fullWidth
        // placeholder={placeholder}
      >
        {inputProps?.selectOptions}
      </Select>
    ),
    date: (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          name={inputProps?.name}
          value={inputProps?.value ? dayjs(inputProps.value as string) : dayjs()}
          onChange={(newValue) => onChangeHandlerForDate(newValue, true, inputProps?.name)}
          slotProps={{
            textField: { sx: { maxWidth: '298px' }, fullWidth: true, variant: 'standard' },
          }}
          format="DD.MM.YYYY"
        />
      </LocalizationProvider>
    ),
    textarea: (
      <Input
        name={inputProps?.name}
        type={inputProps?.inputType}
        value={inputProps?.value}
        onChange={(e) => onChangeHandler(e)}
        sx={{ maxWidth: '298px' }}
        fullWidth
        multiline
        minRows={inputProps?.mitTextAreaRows ?? 6}
        placeholder={placeholder}
      />
    ),
    chip: (
      <Stack direction="row" flexWrap="wrap" gap={1}>
        {addChipInputShown && (
          <Stack direction="row" sx={{ flexGrow: 1, width: '100%', maxWidth: '298px' }}>
            <Input
              name="new_chip_element"
              value={newChipValue}
              onChange={(e) => setNewChipValue(e.target.value)}
              fullWidth
            />
            <IconButton size="small" aria-label="save" color="success" onClick={saveNewChipValue}>
              <CheckIcon />
            </IconButton>
            <IconButton size="small" aria-label="cancel" color="error" onClick={hideAddChipInput}>
              <CancelIcon />
            </IconButton>
          </Stack>
        )}

        {!addChipInputShown && (
          <IconButton size="small" aria-label="add" onClick={showAddChipInput}>
            <AddIcon />
          </IconButton>
        )}

        {getChipElements(true)}
      </Stack>
    ),
  };

  if (hidden) return null;
  if (!value && !withEmptyValue) return null;

  return (
    <>
      <Stack direction="row" spacing={2} alignItems={editProps?.isEditMode ? 'center' : 'initial'}>
        <Typography sx={nameSx ?? { width: '120px' }} color="darkslategray">
          {name}
        </Typography>

        <Box sx={{ maxWidth: maxWidthValue ?? '360px', width: '100%' }}>
          {editProps?.withEditMode && editProps?.isEditMode && !isLoading ? (
            editComponentMap[componentType]
          ) : isChip ? (
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {getChipElements()}
            </Stack>
          ) : (
            (component ?? (
              <Typography sx={{ whiteSpace: breakSpaces ? 'break-spaces' : 'normal' }}>
                {formattedValue ?? value}
              </Typography>
            ))
          )}
        </Box>
      </Stack>

      {hint && (
        <Typography variant="caption" color="warning.main">
          {hint}
        </Typography>
      )}

      {!withoutDivider && <Divider />}
    </>
  );
};
